import { ApiStatus, IWorkflow } from '../../models';
import {
  ILoadedWorkflowAction,
  IUpdatedWorkflowAction,
  WorkflowsAction,
  WorkflowsActionTypes,
} from '../actions/workflowActions';

export const initialWorkflowState: IWorkflowState = {
  loadingListStatus: null,
  loadingStatus: null,
  updatingStatus: null,
  reportWorkflows: [],
  reportWorkflowsStatus: null,
  workflow: null,
  workflows: [],
}

export interface IWorkflowState {
  loadingListStatus: ApiStatus | null;
  loadingStatus: ApiStatus | null;
  updatingStatus: ApiStatus | null;
  reportWorkflows: IWorkflow[];
  reportWorkflowsStatus: ApiStatus | null;
  workflow: IWorkflow | null;
  workflows: IWorkflow[]; // all workflows
}

export default function workflowsReducer(
  state: IWorkflowState = initialWorkflowState,
  action: WorkflowsAction
): IWorkflowState {

  switch (action.type) {
    case WorkflowsActionTypes.LOAD_WORKFLOWS:
      return {
        ...state,
        loadingListStatus: ApiStatus.LOADING,
      }
    case WorkflowsActionTypes.LOADING_WORKFLOWS_FAILED:
      return {
        ...state,
        loadingListStatus: ApiStatus.FAILED,
      }
    case WorkflowsActionTypes.LOADED_WORKFLOWS:
      /*
       * Loaded only once per lifecycle;
       * Represents all workflows per workspace.
       */
      return {
        ...state,
        workflows: action.payload.workflows,
        loadingListStatus: ApiStatus.LOADED,
      };

    case WorkflowsActionTypes.LOAD_REPORT_WORKFLOWS:
      return {
        ...state,
        reportWorkflowsStatus: ApiStatus.LOADING
      };
    case WorkflowsActionTypes.LOADING_REPORT_WORKFLOWS_FAILED:
      return {
        ...state,
        reportWorkflowsStatus: ApiStatus.FAILED
      };
    case WorkflowsActionTypes.LOADED_REPORT_WORKFLOWS:
      return {
        ...state,
        reportWorkflowsStatus: ApiStatus.LOADED,
        reportWorkflows: action.payload.workflows,
      };

    case WorkflowsActionTypes.LOAD_WORKFLOW:
    case WorkflowsActionTypes.LOADING_WORKFLOW:
      return {
        ...state,
        loadingStatus: ApiStatus.LOADING
      };

    case WorkflowsActionTypes.LOADING_WORKFLOW_FAILED:
      return {
        ...state,
        loadingStatus: ApiStatus.FAILED
      };

    case WorkflowsActionTypes.LOADED_WORKFLOW:
      return handleLoadedWorkflow(state, action);
    case WorkflowsActionTypes.UNSET_WORKFLOWS:
      return {
        ...state,
        loadingListStatus: null,
        reportWorkflows: [],
        reportWorkflowsStatus: null,
        updatingStatus: null,
      };

    case WorkflowsActionTypes.SAVE_WORKFLOW:
    case WorkflowsActionTypes.UPDATE_WORKFLOW:
      return {
        ...state,
        updatingStatus: ApiStatus.LOADING,
      };

    case WorkflowsActionTypes.SAVED_WORKFLOW:
      return {
        ...state,
        updatingStatus: ApiStatus.LOADED,
      };

    case WorkflowsActionTypes.UPDATED_WORKFLOW:
      return handledUpdatedWorkflow(state, action);
    case WorkflowsActionTypes.SAVING_WORKFLOW_FAILED:
    case WorkflowsActionTypes.UPDATING_WORKFLOW_FAILED:
      return {
        ...state,
        updatingStatus: ApiStatus.FAILED,
      };

    case WorkflowsActionTypes.REMOVED_WORKFLOW:
      const removeWorkflowFilter = (workflow: IWorkflow) => workflow.uid !== action.payload;

      return {
        ...state,
        reportWorkflows: [...state.reportWorkflows.filter(removeWorkflowFilter)],
        workflows: [...state.workflows.filter(removeWorkflowFilter)]
      }

    default:
      return state;
  }
}

function handledUpdatedWorkflow(
  state: IWorkflowState,
  action: IUpdatedWorkflowAction
): IWorkflowState {
  const update = (workflow: IWorkflow) => {
    if (action.payload.uid === workflow.uid) {
        let recipientsJson = workflow.recipients_json;
        let profileJson = workflow.profile_json;
        let nextRun = (action.payload.next_run || '').replace('+00:00', '');

        /*
         * update recipients_json and profile_json with recipients and profile in case it's set
         */
        if (action.payload.recipients) {
          recipientsJson = JSON.parse(action.payload.recipients);
        }
        if (action.payload.profile) {
          profileJson = JSON.parse(action.payload.profile);
        }

        return {
          ...workflow,
          ...action.payload,
          recipients_json: recipientsJson,
          profile_json: profileJson,
          next_run: nextRun,
        }
      }

      return {
        ...workflow,
      }
  }

  return {
    ...state,
    reportWorkflows: state.reportWorkflows.map(update),
    workflows: state.workflows.map(update),
    updatingStatus: ApiStatus.LOADED,
  }
}

function handleLoadedWorkflow(
  state: IWorkflowState,
  action: ILoadedWorkflowAction
): IWorkflowState {
  const workflowExists: boolean = !!state.workflows
    .find((workflow: IWorkflow) => workflow.uid === action.payload.workflow.uid);

  return {
    ...state,
    loadingStatus: ApiStatus.LOADED,
    reportWorkflows: workflowExists ? state.reportWorkflows : [
      ...state.reportWorkflows,
      action.payload.workflow
    ],
    workflows: workflowExists ? state.workflows : [
      ...state.workflows,
      ...[action.payload.workflow]
    ],
  }
}
